import { CSSProperties, useEffect, useRef, useState } from 'react';
import { useAdminConsoleApiContext, useToastApiContext, useVivliConfigContext } from '@vivli/core/infrastructure/context';
import { useAdminConsoleContext, useAdminConsoleLookupIdContext } from '@vivli/features/admin-console/infrastructure/context';
import { IDataRequest } from '@vivli/features/data-requests/infrastructure/interface';
import { useResearchTeamMembers } from '@vivli/features/data-requests/infrastructure/hook';
import { finalize, first } from 'rxjs/operators';
import { CheckboxComponent, DatePickerComponent, DropdownFieldComponent, TextFieldComponent } from '@vivli/shared/components';
import { IProvisionAdminOptions } from '@vivli/features/admin-console/infrastructure/interface';
import moment from 'moment';
import { VMSizeEnum, VmStatusEnum } from '@vivli/features/virtual-machine/infrastructure/enum';
import { useCopyToClipboardHook, useVmPubSubHook } from '@vivli/shared/infrastructure/hook';
import { IVirtualMachine, IVirtualMachineUser } from '@vivli/features/virtual-machine/infrastructure/interface';
import { VmUserDetailComponent } from './vm-user-details.component';
import { ButtonView, LoadIndicatorView } from '@vivli/shared/views';
import { useModalApiContext } from '@vivli/core/infrastructure/context';
import { auditHelperTextStyle, CREATE_AUDIT_RECORD_BUTTON_LABEL, CREATE_AUDIT_RECORD_HELPER_TEXT } from './create-audit-record.constants';

const dropdownButtonStyle: CSSProperties = {
    marginLeft: '10px'
};

const marginLeftStyle: CSSProperties = {
    marginLeft: 10
};
const marginBottom5Style: CSSProperties = {
    marginBottom: '5px'
};
const fontBoldStyle: CSSProperties = {
    fontWeight: '700'
};
const templateSectionStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '40% auto',
    width: '75%',
    marginBottom: '5px'
};
const lookupSectionStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto auto auto',
    columnGap: '10px',
    width: '55%',
    marginBottom: '20px',
    alignItems: 'center'
};

const dropdownSectionStyle: CSSProperties = {
    margin: '15px 0',
    display: 'grid',
    gridTemplateColumns: '200px 350px 200px'
};

const statusSectionStyle: CSSProperties = {
    display: 'grid',
    gridAutoColumns: 'auto',
    columnGap: '10px',
    alignItems: 'center',
    marginTop: '10px',
    width: '80%',
    marginBottom: '20px'
};
const buttonStyle: CSSProperties = {
    marginLeft: 15
};
const marginLeft50Style: CSSProperties = {
    marginLeft: 50
};
const templateBodyStyle: CSSProperties = {
    overflow: 'auto',
    paddingRight: '40px'
};

const fiveColStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'auto auto auto auto auto',
    columnGap: '20px',
    marginBottom: '20px'
};

const twoColStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '20% 30%',
    columnGap: '20px',
    marginBottom: '20px'
};

const fourColStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto auto',
    columnGap: '20px',
    marginBottom: '20px'
};

export function AdminVirtualMachineComponent() {
    const dropDownResetValue = Object.values(VmStatusEnum).map((value) => ({ title: value, value: value }));

    const useLookUpRef = useRef<any>();
    const modalService = useModalApiContext();
    const adminConsoleService = useAdminConsoleApiContext();
    const [isLoading, setIsLoading] = useState(false);
    const [dataRequest, setDataRequest] = useState<IDataRequest>();
    const [virtualMachine, setVirtualMachine] = useState<IVirtualMachine>();
    const [deprovisionedDate, setDeprovisionedDate] = useState<Date>();
    const [selectedStatus, setSelectedStatus] = useState<string>(null);
    const [userVmDetails, setUserVmDetails] = useState<IVirtualMachineUser[]>([]);
    const [researchVmType, setResearchVmType] = useState([]);
    const [selectedVmSize, setSelectedVmSize] = useState<string>();
    const [sasSelected, setSasSelected] = useState(false);
    const [stataSelected, setStataSelected] = useState(false);
    const [operationStatus, setOperationStatus] = useState<string>();
    const vivliConfig = useVivliConfigContext();
    const context = useAdminConsoleContext();
    const toastService = useToastApiContext();
    const { copyToClipboard } = useCopyToClipboardHook();
    const { vmLookupId, setVmLookupId } = useAdminConsoleLookupIdContext();
    const { getLeadResearcher } = useResearchTeamMembers();

    const btnClick = (action: string, isAsync = false) => {
        vmPubSub.subscribeToVmStatusChanged();
        vmPubSub.subscribeToVmMessages(updateProcessingMessages);
        context.handleAction(action, virtualMachine?.id, null, isAsync);
    };
    const handleError = (error) => {
        modalService.error(error);
    };

    const handleInputChangeVmRequest = (e) => {
        const value = e.currentTarget.value;
        setVmLookupId(value);
    };
    const handleLookUpClick = (e) => {
        e.stopPropagation();
        const requestId = vmLookupId.padStart(8, '0');
        if (virtualMachine && virtualMachine?.dataRequestDoi != requestId) {
            vmPubSub.CloseChannel();
            setOperationStatus(null);
        }
        updateVMStatus();
    };

    const updateVMStatus = () => {
        setIsLoading(true);
        const requestId = vmLookupId.padStart(8, '0');
        adminConsoleService
            .getVMData(requestId)
            .pipe(
                first(),
                finalize(() => setIsLoading(false))
            )
            .subscribe((data) => {
                setDataRequest(data.item2);
                let virtualMachine = { ...data.item1 };
                if (!data.item1) {
                    virtualMachine = {
                        ...virtualMachine,
                        id: '',
                        status: VmStatusEnum.vmStatusNotCreated,
                        size: VMSizeEnum.None,
                        licenseSAS: false,
                        licenseSTATA: false,
                        deProvisionedDateTime: null,
                        deletedOnDateTime: null
                    };
                }
                addVmUserDetails(data);
                setVirtualMachine(virtualMachine);
            }, handleError);
    };
    const vmPubSub = useVmPubSubHook(virtualMachine, updateVMStatus);

    const addVmUserDetails = (details) => {
        if (!details.item1) {
            setUserVmDetails([]);
            return;
        }
        const authorizedUsersDetails = details.item1.authorizedUsers;
        const researchTeamDetails = details.item2.researchTeam?.researchTeamDetails;

        const userDetails = authorizedUsersDetails
            .filter((authedUser) => researchTeamDetails.some((rtd) => rtd.userId === authedUser.id))
            .map((authedUser) => {
                const teamMember = researchTeamDetails.find((teamMember) => teamMember.userId === authedUser.id);
                return {
                    email: teamMember.email,
                    id: authedUser.id,
                    name: teamMember.firstName + ' ' + teamMember.lastName,
                    username: authedUser.username
                };
            });
        setUserVmDetails(userDetails);
    };
    const leadResearcherName = () => {
        const lead = getLeadResearcher(dataRequest?.researchTeam);
        return (
            <span>
                {' '}
                {lead?.firstName}
                {lead?.lastName},{' '}
                {lead?.email}
            </span>
        );
    };

    const dateLookUp = (date: Date | string) => {
        return <span>{!date || date === 'Not Set' ? 'Not Set' : moment(date).format('YYYY/DD/MM')}</span>;
    };

    const handleVMProvisioning = () => {
        //options are hardcoded for now
        const VmOptions: IProvisionAdminOptions = {
            licenseSAS: sasSelected,
            licenseSTATA: stataSelected,
            size: selectedVmSize,
            comment: ''
        };
        context.provisionVm(dataRequest?.id, VmOptions, () => vmPubSub.startVmPolling());
    };

    const handleStatusReset = (e) => {
        e.stopPropagation();
        context.handleAction('ResetVmStatus', virtualMachine?.id, selectedStatus, false);
    };
    const handleSetDeprovisionDate = (e) => {
        e.stopPropagation();
        context.handleAction('SetDeprovisionedDate', virtualMachine?.id, deprovisionedDate.toDateString(), false);
    };

    const handleCopyToClipboardClick = () => {
        const parsedLog = virtualMachine?.provisioningLog.join('\r\n');
        copyToClipboard(parsedLog).then(() => {
            toastService.success('provisioning Log copied to clipboard.');
        });
    };
    const updateProcessingMessages = (message: any) => {
        if (message?.data) {
            setOperationStatus(message.data);
        } else {
            setOperationStatus(null);
        }
    };

    useEffect(() => {
        vmPubSub.initiatePubSubIfNeeded(true, updateProcessingMessages); //initiate subscription for certain statuses
    }, [virtualMachine]);

    useEffect(() => {
        if (!vivliConfig) {
            return;
        }
        const vmSizeList = Object.values<{ title: string; vmSize: string }>(vivliConfig.virtualMachineTypes).map((value) => ({
            title: value.title,
            value: value.vmSize
        }));
        setResearchVmType(vmSizeList);
        return () => {
            vmPubSub.closeConnections();
        };
    }, [vivliConfig]);
    return (
        <div>
            <div style={lookupSectionStyle}>
                <span style={fontBoldStyle}>VM Request ID:</span>
                <TextFieldComponent
                    label={'VM Request Id'}
                    value={vmLookupId}
                    onKeyDown={(e) => {
                        e.key === 'Enter' && handleLookUpClick(e);
                    }}
                    style={marginLeftStyle}
                    onChange={(e) => handleInputChangeVmRequest(e)}
                />
                <ButtonView style={buttonStyle} onClick={handleLookUpClick} isLoading={isLoading} disabled={isLoading}>
                    Lookup VM
                </ButtonView>
                <ButtonView
                    style={buttonStyle}
                    onClick={() => context.handleAction('CreateVmAuditRecord', virtualMachine?.id, null, false)}
                    disabled={!virtualMachine?.id}
                >
                    {CREATE_AUDIT_RECORD_BUTTON_LABEL}
                </ButtonView>
                <span style={auditHelperTextStyle}>{CREATE_AUDIT_RECORD_HELPER_TEXT}</span>
            </div>

            {virtualMachine && (
                <div style={templateBodyStyle}>
                    <div style={templateSectionStyle}>
                        <div>
                            <span style={fontBoldStyle}>Title:</span> {dataRequest?.requestTitle}
                        </div>
                        <div>
                            <span style={fontBoldStyle}>VM Size:</span> {virtualMachine?.size}
                        </div>
                    </div>
                    <div style={templateSectionStyle}>
                        <div>
                            <span style={fontBoldStyle}>Lead Researcher:</span> {leadResearcherName()}
                        </div>
                        <div>
                            <span style={fontBoldStyle}>SAS:</span> {virtualMachine?.licenseSAS ? 'Yes' : 'No'}
                        </div>
                    </div>
                    <div style={templateSectionStyle}>
                        <div>
                            <span style={fontBoldStyle}>Current Status:</span> {virtualMachine?.status}
                        </div>
                        <div>
                            <span style={fontBoldStyle}>STATA:</span> {virtualMachine?.licenseSTATA ? 'Yes' : 'No'}
                        </div>
                    </div>
                    <div style={marginBottom5Style}>
                        <span style={fontBoldStyle}>Deprovisioned Date:</span>{' '}
                        {dateLookUp(virtualMachine?.deProvisionedDateTime || 'Not Set')}
                    </div>
                    <div style={marginBottom5Style}>
                        <span style={fontBoldStyle}>Deleted Date:</span> {dateLookUp(virtualMachine?.deletedOnDateTime || 'Not Set')}
                    </div>
                    <div>
                        <span style={fontBoldStyle}>Operation Status:</span> {operationStatus || 'Not Set'}
                    </div>

                    {isLoading && (
                        <div style={statusSectionStyle}>
                            <LoadIndicatorView />
                        </div>
                    )}

                    {!isLoading && virtualMachine && (
                        <div>
                            <div style={dropdownSectionStyle}>
                                <div>Status to set</div>
                                <DropdownFieldComponent items={dropDownResetValue} label={'Status to set'} onChange={setSelectedStatus} />
                                <ButtonView onClick={handleStatusReset} style={dropdownButtonStyle}>
                                    Reset Status
                                </ButtonView>
                            </div>
                            <div style={dropdownSectionStyle}>
                                <div>Deprovisioned Date to set</div>
                                <DatePickerComponent
                                    ref={useLookUpRef}
                                    label={'Deprovisioned Date'}
                                    onChange={setDeprovisionedDate}
                                    placeholderText={'MM/DD/YY'}
                                    value={deprovisionedDate?.toDateString()}
                                />
                                <ButtonView style={dropdownButtonStyle} onClick={handleSetDeprovisionDate}>
                                    Set Deprovisioned Date
                                </ButtonView>
                            </div>
                            <div style={twoColStyle}>
                                <ButtonView onClick={() => btnClick('DeleteVmRecord')}>Delete VM Record</ButtonView>
                                <ButtonView style={marginLeft50Style} onClick={handleCopyToClipboardClick}>
                                    Copy Provisioning Log to Clipboard
                                </ButtonView>
                            </div>
                            <div style={fiveColStyle}>
                                <ButtonView onClick={() => btnClick('StartVm', true)}>Start</ButtonView>
                                <ButtonView onClick={() => btnClick('StopVm', true)}>Stop</ButtonView>
                                <ButtonView onClick={() => btnClick('DeprovisionVm', true)}>Deprovision</ButtonView>
                                <ButtonView onClick={() => btnClick('RetryProvisioning', true)}>Retry Provisioning</ButtonView>
                                <ButtonView onClick={() => btnClick('AddDataPackages', true)}>Add Data Packages</ButtonView>
                            </div>
                            <div style={fourColStyle}>
                                <ButtonView onClick={() => handleVMProvisioning()}>Provision VM</ButtonView>
                                <DropdownFieldComponent items={researchVmType} label={'Select to provision'} onChange={setSelectedVmSize} />
                                <CheckboxComponent
                                    label={'SAS'}
                                    checked={sasSelected}
                                    onChange={(e) => setSasSelected(e.currentTarget.checked)}
                                />
                                <CheckboxComponent
                                    label={'Stata'}
                                    checked={stataSelected}
                                    onChange={(e) => setStataSelected(e.currentTarget.checked)}
                                />
                            </div>
                            <VmUserDetailComponent vmUserDetails={userVmDetails} vm={virtualMachine}></VmUserDetailComponent>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
